import { GetStaticPaths, GetStaticProps } from 'next';

import { getServerPageGetRegioExpresNewsArticleSlugs, getServerPageGetRegioExpresNewsDetail } from 'api/graphql-helper';
import client, { httpLink, setPreviewSecret } from 'api/apollo-client';
import {
	BodyTextFieldsFragment,
	RegioExpresNewsArticleFieldsFragment,
	RegioExpresNewsDetailSingleFieldsFragment,
	Enum_Componentinternaldefaultmoduleoptions_Backgroundcolor,
	Enum_Componentinternaldefaultmoduleoptions_Padding,
	RegioExpresInformationFieldsFragment,
	Tag
} from 'api/graphql-types';

import Layout from 'modules/layout/layout';
import { ModuleBuilder } from 'modules/module-builder/module-builder';

import IntroHeader from 'modules/intro-header/intro-header';
import BodyText from 'modules/body-text/normal/normal-body-text';
import { BaseModule } from 'modules/base-module/base-module';
import { Head } from 'modules/head/head';

import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import { IBasePageProps } from 'types/page';
import usePageProtection, { IUsePageProtectionVariables } from 'utils/hooks/use-page-protection';
import PasswordLayout from 'modules/password-layout';
import { buildPasswordProtectionStaticProps } from 'utils/password-protection-static';
import { ROUTES } from 'constants/routes';
import { buildSlugPaths } from 'utils/build-slug-paths';

interface IRegioExpresNewsDetailPageProps extends IBasePageProps {
	pageInformation?: RegioExpresInformationFieldsFragment;
	newsDetail?: RegioExpresNewsDetailSingleFieldsFragment;
	newsArticleBySlug?: RegioExpresNewsArticleFieldsFragment;
	id?: string;
	variables?: IUsePageProtectionVariables;
	isPasswordProtected?: boolean;
	passwordPageLayout: any | null;
}

const RegioExpresNewsDetailPage = (props: IRegioExpresNewsDetailPageProps): React.JSX.Element => {
	const { page, PasswordModule } = usePageProtection({
		pageData: props.newsArticleBySlug || null,
		pageQuery: () => getServerPageGetRegioExpresNewsDetail({ variables: props.variables }, client)
	});

	return (
		<Layout
			contentId={props?.id}
			typeName={props?.newsArticleBySlug?.__typename}
			navbar={props?.pageInformation?.navbar}
			footer={props?.pageInformation?.footer}
		>
			<Head seoModule={page?.seo} seoParent={props?.pageInformation?.seo} />
			{page && page.__typename === 'RegioExpresNewsarticle' ? (
				<>
					<BaseModule
						defaultModuleOptions={{
							backgroundColor: page.header
								?.backgroundColor as Enum_Componentinternaldefaultmoduleoptions_Backgroundcolor,
							padding: 'none' as Enum_Componentinternaldefaultmoduleoptions_Padding
						}}
					>
						<IntroHeader
							module={{
								title: page.header?.title || '',
								backgroundColor: page.header?.backgroundColor,
								excerpt: page.header?.excerpt || '',
								cover: page.header?.cover,
								tags: (page.tags as Tag[]) || []
							}}
							slug={page.slug}
						/>
					</BaseModule>
					<BaseModule defaultModuleOptions={page.bodyText?.defaultModuleOptions}>
						<BodyText module={page.bodyText as BodyTextFieldsFragment} publishedAt={page.firstPublishedAt} />
					</BaseModule>
					<ModuleBuilder modules={page.modules || []} slug={page.slug} />
					<ModuleBuilder modules={props?.newsDetail?.modules || []} slug={page.slug} />
				</>
			) : props.isPasswordProtected ? (
				<PasswordLayout
					locale={props.variables?.locale}
					slug={props.variables?.slug}
					pageData={props.passwordPageLayout}
				>
					<PasswordModule />
				</PasswordLayout>
			) : null}
		</Layout>
	);
};

export const getStaticPaths: GetStaticPaths = async (ctx) => {
	const result = await getServerPageGetRegioExpresNewsArticleSlugs({}, client);
	const slugs = result.props.data.findAllSlugs;

	return {
		paths: buildSlugPaths(slugs, ROUTES.NEWS_ARTICLES.slug, ctx.defaultLocale),
		fallback: 'blocking'
	};
};

export const getStaticProps: GetStaticProps = async (ctx) => {
	try {
		const { locale, draftMode, publicationState } = getStrapiParametersFromContext(ctx);
		const slug = ctx.params?.slug ? `/${ROUTES.NEWS_ARTICLES.slug}/${ctx.params.slug}` : '/';
		const variables = { slug, locale, publicationState };

		if (publicationState === 'DRAFT') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const passwordResult = await buildPasswordProtectionStaticProps<any, any, any>({
			fullSlug: slug,
			locale,
			publicationState,
			draftMode,
			contentTypeUid: 'api::regio-expres-newsarticle.regio-expres-newsarticle',
			contentTypeName: 'regio-expres-newsarticle',
			neutralStyle: true,
			hideLanguageSwitcher: true,
			isSubsite: true,
			fetchPage: () => getServerPageGetRegioExpresNewsDetail({ variables }, client),
			extractInformation: (data) => data?.props?.data?.regioExpresInformation?.data?.attributes,
			extractPage: (data) => {
				const node = data?.props?.data?.regioExpresNewsarticleBySlug?.data;
				return node ? { documentId: node.documentId, attributes: node.attributes } : null;
			}
		});

		let newsDetail: any = null;
		if (!passwordResult.isPasswordProtected && passwordResult.page) {
			const detailResult = await getServerPageGetRegioExpresNewsDetail({ variables }, client);
			newsDetail = detailResult?.props?.data?.regioExpresNewsDetail?.data?.attributes || null;
		}

		if (!passwordResult.isPasswordProtected && !passwordResult.page) {
			return { notFound: true };
		}

		return {
			props: {
				id: passwordResult.page?.documentId || null,
				variables,
				pageInformation: passwordResult.pageInformation,
				newsArticleBySlug: passwordResult.page as any,
				newsDetail: newsDetail,
				isPasswordProtected: passwordResult.isPasswordProtected,
				passwordPageLayout: passwordResult.passwordPageLayout,
				initialReduxState: passwordResult.initialReduxState
			},
			revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_TIME_IN_SECONDS)
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('Error in news detail', error);
		return { notFound: true };
	}
};

export default RegioExpresNewsDetailPage;
